import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, TrendingUp, Calendar, Crown, ChevronRight, Sparkles } from 'lucide-react';
import DashboardLayout from '@/components/mafia/DashboardLayout';
import { useMafiaAuth } from '@/context/MafiaAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DashboardOverviewPage() {
  const navigate = useNavigate();
  const { gamer, loading } = useMafiaAuth();
  const [matches, setMatches] = useState([]);
  const [badges, setBadges] = useState([]);

  useEffect(() => {
    if (!loading && !gamer) navigate('/mafia/login', { replace: true });
  }, [loading, gamer, navigate]);

  useEffect(() => {
    if (!gamer) return;
    (async () => {
      try {
        const [m, p] = await Promise.all([
          fetch(`${API}/api/mafia/auth/me/matches?limit=5`, { credentials: 'include' }).then(r => r.ok ? r.json() : { matches: [] }),
          fetch(`${API}/api/mafia/auth/profile/${gamer.member_no}`, { credentials: 'include' }).then(r => r.ok ? r.json() : { badges: [] }),
        ]);
        setMatches(m.matches || []);
        setBadges(p.badges || []);
      } catch { /* ignore */ }
    })();
  }, [gamer]);

  if (loading || !gamer) {
    return <div className="ms-page flex items-center justify-center ms-text-3">Loading…</div>;
  }

  const winRate = gamer.games_played ? Math.round((gamer.wins / gamer.games_played) * 100) : 0;
  const firstName = (gamer.name || '').split(' ')[0] || 'player';

  return (
    <DashboardLayout title={`Welcome back, ${firstName}`} subtitle="Overview">
      {/* Hero greeting card with ember accent */}
      <section
        className="ms-card p-6 sm:p-8 mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-8"
        data-testid="overview-hero"
        style={{ background: 'linear-gradient(135deg, var(--ms-surface-1) 0%, var(--ms-surface-2) 100%)' }}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="ms-text-ember" />
            <p className="ms-kicker ms-kicker-ember">{gamer.rank} member</p>
          </div>
          <h2 className="ms-display text-3xl sm:text-4xl leading-tight">
            {gamer.points > 0 ? `${gamer.points} points and climbing` : 'Ready for your first table?'}
          </h2>
          <p className="ms-text-2 text-sm mt-3 leading-relaxed max-w-md">
            {gamer.games_played > 0
              ? `${gamer.games_played} ${gamer.games_played === 1 ? 'game' : 'games'} played · ${winRate}% win rate`
              : 'Hosts will link you to a seat the next time you join a table.'}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => navigate('/dashboard/card')}
            data-testid="overview-view-card"
            className="ms-btn ms-btn-secondary"
          >
            View card
          </button>
          <button
            onClick={() => navigate(`/p/${gamer.member_no}`)}
            data-testid="overview-view-profile"
            className="ms-btn ms-btn-primary"
          >
            Public profile
          </button>
        </div>
      </section>

      {/* KPIs */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Kpi label="Points" value={gamer.points} icon={Trophy} accent testid="kpi-points" />
        <Kpi label="Games" value={gamer.games_played} icon={Calendar} testid="kpi-games" />
        <Kpi label="Wins" value={gamer.wins} icon={Crown} testid="kpi-wins" />
        <Kpi label="Win rate" value={`${winRate}%`} icon={TrendingUp} testid="kpi-winrate" />
      </section>

      {/* Recent + Achievements */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section className="ms-card lg:col-span-2">
          <PanelHeader title="Recent matches" action={
            <button
              onClick={() => navigate('/dashboard/history')}
              data-testid="overview-view-all-history"
              className="text-xs ms-text-2 hover:ms-text flex items-center gap-1 font-medium"
            >
              View all <ChevronRight size={12} />
            </button>
          } />
          {matches.length === 0 ? (
            <EmptyState text="No matches yet. Hosts will assign you to seats once you start playing." />
          ) : (
            <div className="ms-divide">
              {matches.map((m) => (
                <MatchRow key={m.game_id} m={m} />
              ))}
            </div>
          )}
        </section>

        <section className="ms-card">
          <PanelHeader title="Achievements" />
          {badges.length === 0 ? (
            <EmptyState text="Play games to unlock badges." />
          ) : (
            <div className="p-4 grid grid-cols-1 gap-2">
              {badges.map((b) => (
                <div
                  key={b.key}
                  data-testid={`badge-${b.key}`}
                  className="flex items-start gap-3 p-3"
                  style={{
                    background: 'var(--ms-ember-soft)',
                    border: '1px solid rgba(224,200,149,0.25)',
                    borderRadius: 'var(--ms-radius-md)',
                  }}
                >
                  <Crown size={14} className="ms-text-ember mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-semibold ms-text-ember">{b.label}</p>
                    <p className="text-[11px] ms-text-2 mt-0.5 leading-snug">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}

function Kpi({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="ms-card ms-card-hover p-5" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <p className="ms-kicker">{label}</p>
        <Icon size={14} className={accent ? 'ms-text-red' : 'ms-text-3'} />
      </div>
      <p className={`ms-display text-3xl tabular-nums ${accent ? 'ms-text-red' : ''}`}>{value}</p>
    </div>
  );
}

function PanelHeader({ title, action }) {
  return (
    <div className="px-5 py-4 ms-border-b flex items-center justify-between">
      <p className="ms-kicker">{title}</p>
      {action}
    </div>
  );
}

function EmptyState({ text }) {
  return <p className="px-5 py-8 text-sm ms-text-2 text-center">{text}</p>;
}

function MatchRow({ m }) {
  return (
    <div className="grid grid-cols-12 items-center px-5 py-3 hover:bg-[var(--ms-surface-2)] transition-colors" data-testid={`match-row-${m.game_id}`}>
      <span className="col-span-3 text-xs ms-text-3">
        {m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}
      </span>
      <span className="col-span-3 text-sm ms-text">Seat #{m.seat}</span>
      <span className="col-span-3 text-xs ms-text-2 capitalize">{m.team || '—'}</span>
      <span className="col-span-3 text-right">
        <span className={`ms-chip ${m.won ? 'ms-chip-red' : ''}`}>
          {m.status === 'ended' ? (m.won ? 'Win' : 'Loss') : (m.status || '—')}
        </span>
      </span>
    </div>
  );
}
